import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatElapsedMs } from '../utils/delegation/elapsedTime';

// 30s, not 1s: every agent-presence surface (card strip, header chip, panel) shows a
// coarse "5 min"/"2h" label, never seconds, so a cheap tick is enough — unlike the modal
// header's own 1s tick, which exists only because that surface used to show MM:SS.
const ELAPSED_TICK_MS = 30_000;

/**
 * Shared elapsed-time label for any surface anchored on a `startedAt` timestamp. Ticks
 * every 30s while `finishedAt` is unset; once it's set, freezes the label at that instant
 * and stops the interval. Returns null when there is no `startedAt` to measure from (no
 * agent presence to show elapsed time for).
 */
export function useElapsedSince(
  startedAt: number | null | undefined,
  finishedAt?: number,
): string | null {
  const { t } = useTranslation();
  const isTicking = startedAt != null && finishedAt === undefined;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isTicking) {
      return;
    }
    const interval = setInterval(() => setNow(Date.now()), ELAPSED_TICK_MS);
    return () => clearInterval(interval);
  }, [isTicking]);

  if (startedAt == null) {
    return null;
  }

  return formatElapsedMs((finishedAt ?? now) - startedAt, t);
}
